import { useMemo } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { trpc, type RouterOutputs } from '@org/clients/school';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  HeroBanner,
  heroBannerChipClass,
  HeroCards,
  type HeroCardsLinkComponentProps,
  Skeleton,
} from '@org/ui';
import { useTranslation } from 'react-i18next';
import { useTeacherId } from '../components/teacher-picker';
import { TeacherAgenda } from '../components/teacher-agenda';
import { FlagPill, FLAG_CATEGORIES, type FlagCategory } from '../components/student-flag-badges';

export const Route = createFileRoute('/')({
  component: DashboardPage,
  validateSearch: (search: Record<string, unknown>) => ({
    teacherId: typeof search.teacherId === 'string' ? search.teacherId : undefined,
  }),
});

const numberFormatter = new Intl.NumberFormat('pt-BR');
const dateFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});
/** How many students / items each dashboard list shows before "and N more". */
const LIST_LIMIT = 5;

type StudentItem = RouterOutputs['teacher']['students']['list']['students'][number];

function DashboardPage() {
  const teacherId = useTeacherId();
  const { data, isLoading } = trpc.teacher.dashboard.summary.useQuery({ teacherId });
  const { data: studentsData, isLoading: isStudentsLoading } = trpc.teacher.students.list.useQuery({ teacherId });
  const { t } = useTranslation('admin');

  const statsConfig = t('teacher.dashboard.stats', { returnObjects: true }) as Array<{
    label: string;
    helper: string;
  }>;
  const statValues = [
    data?.totalStudents ?? 0,
    data?.feedbacksToSend ?? 0,
    data?.pendingScorecards ?? 0,
  ];
  const stats = statsConfig.map((stat, index) => ({
    ...stat,
    value: statValues[index] ?? 0,
  }));
  const clickablePaths = ['/students', '/feedback', '/classes'];
  const heroCards = stats.map((stat, index) => ({
    key: stat.label,
    title: stat.label,
    value: numberFormatter.format(stat.value),
    description: stat.helper,
    href: clickablePaths[index] ? { to: clickablePaths[index] } : undefined,
  }));
  const isHeroCardsLoading = isLoading && !data;
  const RouterLink = ({ to, params, className, children }: HeroCardsLinkComponentProps) => (
    <Link to={to} params={params} className={className}>
      {children}
    </Link>
  );
  const chips = t('teacher.dashboard.hero.chips', { returnObjects: true }) as string[];

  // Red flags, worst first: more red categories, then a red flag of the teacher's own, then name.
  const redStudents = useMemo(() => {
    const students = studentsData?.students ?? [];
    const redCount = (student: StudentItem) =>
      FLAG_CATEGORIES.filter((category) => student.autoFlag.categories[category].level === 'red').length;
    return students
      .filter((student) => student.autoFlag.level === 'red' || student.teacherFlag?.flag === 'red')
      .sort((a, b) => {
        const byCount = redCount(b) - redCount(a);
        if (byCount !== 0) return byCount;
        const byOwn = Number(b.teacherFlag?.flag === 'red') - Number(a.teacherFlag?.flag === 'red');
        if (byOwn !== 0) return byOwn;
        return a.displayName.localeCompare(b.displayName);
      });
  }, [studentsData]);

  // Students the teacher has not reacted to yet: red automatic flag and no flag of her own.
  const toContact = useMemo(
    () => redStudents.filter((student) => student.autoFlag.level === 'red' && !student.teacherFlag),
    [redStudents],
  );

  return (
    <div className="flex w-full flex-col gap-10 pb-16">
      <HeroBanner
        icon={
          <img
            aria-hidden="true"
            src="/icon-hand-small-white.png"
            alt=""
            className="h-12 w-12 object-contain"
          />
        }
        title={t('teacher.dashboard.greeting.line1', {
          name: data?.teacherFirstName ?? t('teacher.dashboard.greeting.nameFallback'),
        })}
        subtitle={t('teacher.dashboard.greeting.line2')}
        chips={
          <>
            {chips.map((chip) => (
              <span key={chip} className={heroBannerChipClass}>
                {chip}
              </span>
            ))}
            <Link to="/classes" search={{ teacherId: undefined }} className={heroBannerChipClass}>
              {t('teacher.dashboard.hero.cta')}
            </Link>
          </>
        }
      >
        <HeroCards
          cards={heroCards}
          isLoading={isHeroCardsLoading}
          loadingCount={heroCards.length}
          LinkComponent={RouterLink}
        />
      </HeroBanner>

      <section className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-8 px-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-8">
          <TodoList
            summary={data}
            isLoading={isLoading}
            toContact={toContact}
            isStudentsLoading={isStudentsLoading}
          />
          <AlertsSummary students={redStudents} isLoading={isStudentsLoading} />
        </div>

        <aside className="min-w-0 lg:sticky lg:top-4 lg:self-start" aria-label={t('teacher.dashboard.agenda.title')}>
          <TeacherAgenda className="lg:max-h-[28rem]" />
        </aside>
      </section>
    </div>
  );
}

function SectionHeading({ eyebrow, title, action }: { eyebrow: string; title: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div>
        <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">{eyebrow}</p>
        <h2 className="text-2xl font-semibold text-gray-900">{title}</h2>
      </div>
      {action}
    </div>
  );
}

function AlertsSummary({ students, isLoading }: { students: StudentItem[]; isLoading: boolean }) {
  const { t } = useTranslation('admin');
  const visible = students.slice(0, LIST_LIMIT);
  const remaining = students.length - visible.length;

  const redCategoriesLabel = (student: StudentItem) =>
    FLAG_CATEGORIES.filter((category: FlagCategory) => student.autoFlag.categories[category].level === 'red')
      .map((category) => t(`teacher.flags.categories.${category}`))
      .join(', ');

  return (
    <div className="space-y-4">
      <SectionHeading
        eyebrow={t('teacher.dashboard.alerts.eyebrow')}
        title={t('teacher.dashboard.alerts.title')}
        action={
          <Button asChild variant="outline" className="rounded-full border border-rose-100 bg-white text-sm font-semibold text-[#cf2e2e]">
            <Link to="/flags" search={{ teacherId: undefined }}>{t('teacher.dashboard.alerts.cta')}</Link>
          </Button>
        }
      />
      <Card className="border border-rose-50 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <CardDescription>{t('teacher.dashboard.alerts.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {isLoading && (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full" />
              ))}
            </div>
          )}
          {!isLoading && students.length === 0 && (
            <p className="rounded-lg border border-dashed border-emerald-200 bg-emerald-50/50 px-4 py-3 text-sm text-emerald-800">
              {t('teacher.dashboard.alerts.empty')}
            </p>
          )}
          {!isLoading &&
            visible.map((student) => {
              const categories = redCategoriesLabel(student);
              return (
                <Link
                  key={student.studentId}
                  to="/students/$studentId"
                  params={{ studentId: student.studentId }}
                  search={{ teacherId: undefined }}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-100 bg-rose-50/40 px-4 py-3 transition hover:bg-rose-50"
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900">{student.displayName}</p>
                    <p className="text-xs text-gray-600">
                      {student.groups[0] ?? t('teacher.students.noGroup')}
                      {categories && <> · {t('teacher.dashboard.alerts.redCategories', { categories })}</>}
                      {student.teacherFlag?.flag === 'red' && (
                        <>
                          {' · '}
                          {t('teacher.dashboard.alerts.yourFlag', {
                            reason: student.teacherFlag.reason
                              ? t(`teacher.flags.reasonCategories.${student.teacherFlag.reason}`)
                              : t('teacher.flags.noReason'),
                          })}
                        </>
                      )}
                    </p>
                  </div>
                  <FlagPill level="red" />
                </Link>
              );
            })}
          {!isLoading && remaining > 0 && (
            <p className="px-1 text-xs text-gray-500">{t('teacher.dashboard.todos.more', { count: remaining })}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

type Summary = RouterOutputs['teacher']['dashboard']['summary'];

function TodoList({
  summary,
  isLoading,
  toContact,
  isStudentsLoading,
}: {
  summary: Summary | undefined;
  isLoading: boolean;
  toContact: StudentItem[];
  isStudentsLoading: boolean;
}) {
  const { t } = useTranslation('admin');
  const formatDate = (value: Date | string | null) => (value ? dateFormatter.format(new Date(value)) : '');

  return (
    <div className="space-y-4">
      <SectionHeading eyebrow={t('teacher.dashboard.todos.eyebrow')} title={t('teacher.dashboard.todos.title')} />
      <div className="grid gap-4 lg:grid-cols-3">
        <TodoCard
          title={t('teacher.dashboard.todos.scorecards')}
          hint={t('teacher.dashboard.todos.scorecardsHint')}
          count={summary?.pendingScorecards ?? 0}
          isLoading={isLoading}
          items={(summary?.pendingScorecardList ?? []).slice(0, LIST_LIMIT).map((event) => ({
            key: event.eventId,
            title: event.title ?? t('common.fallbacks.untitledClass'),
            subtitle: [event.groupLabel ?? t('teacher.dashboard.todos.noGroup'), formatDate(event.startTime)].join(' · '),
            action: t('teacher.dashboard.todos.fill'),
            href: `/event/${event.eventId}/scorecard/new`,
          }))}
          moreHref="/classes"
        />
        <TodoCard
          title={t('teacher.dashboard.todos.materials')}
          hint={t('teacher.dashboard.todos.materialsHint')}
          count={summary?.unassignedUpcomingCount ?? 0}
          isLoading={isLoading}
          items={(summary?.unassignedUpcoming ?? []).slice(0, LIST_LIMIT).map((event) => ({
            key: event.eventId,
            title: event.title ?? t('common.fallbacks.untitledClass'),
            subtitle: [event.groupLabel ?? t('teacher.dashboard.todos.noGroup'), formatDate(event.startTime)].join(' · '),
            action: t('teacher.dashboard.todos.assign'),
            href: '/next-classes',
          }))}
          moreHref="/next-classes"
        />
        <TodoCard
          title={t('teacher.dashboard.todos.contact')}
          hint={t('teacher.dashboard.todos.contactHint')}
          count={toContact.length}
          isLoading={isStudentsLoading}
          items={toContact.slice(0, LIST_LIMIT).map((student) => ({
            key: student.studentId,
            title: student.displayName,
            subtitle: student.groups[0] ?? t('teacher.students.noGroup'),
            action: t('teacher.dashboard.todos.open'),
            href: `/students/${student.studentId}`,
          }))}
          moreHref="/flags"
        />
      </div>
    </div>
  );
}

function TodoCard({
  title,
  hint,
  count,
  isLoading,
  items,
  moreHref,
}: {
  title: string;
  hint: string;
  count: number;
  isLoading: boolean;
  items: Array<{ key: string; title: string; subtitle: string; action: string; href: string }>;
  moreHref: string;
}) {
  const { t } = useTranslation('admin');
  const remaining = count - items.length;
  const done = !isLoading && count === 0;

  return (
    <Card className={`border bg-white shadow-sm ${done ? 'border-emerald-100' : 'border-rose-50'}`}>
      <CardHeader className="space-y-1 pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base text-gray-900">{title}</CardTitle>
          <Badge className={done ? 'bg-emerald-100 text-emerald-700' : 'bg-[#cf2e2e] text-white'}>
            {isLoading ? '…' : numberFormatter.format(count)}
          </Badge>
        </div>
        <CardDescription className="text-xs">{hint}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading && (
          <>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-5/6" />
          </>
        )}
        {done && <p className="text-sm text-emerald-700">{t('teacher.dashboard.todos.allDone')}</p>}
        {!isLoading &&
          items.map((item) => (
            // Plain anchors: these targets live in this app, and a string href
            // keeps the strict typed-router search params out of the way.
            <a
              key={item.key}
              href={item.href}
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 px-3 py-2 text-sm transition hover:border-rose-200 hover:bg-rose-50/40"
            >
              <span className="min-w-0">
                <span className="block font-medium leading-snug text-gray-900">{item.title}</span>
                <span className="block truncate text-xs text-gray-500">{item.subtitle}</span>
              </span>
              <span className="shrink-0 text-xs font-semibold text-[#cf2e2e]">{item.action}</span>
            </a>
          ))}
        {!isLoading && remaining > 0 && (
          <a href={moreHref} className="block px-1 text-xs text-gray-500 hover:text-[#cf2e2e]">
            {t('teacher.dashboard.todos.more', { count: remaining })}
          </a>
        )}
      </CardContent>
    </Card>
  );
}
